/* eslint-disable max-statements, max-lines, complexity */

import React, { useState } from 'react'
import { func, number, object, oneOfType, shape, string } from 'prop-types'
import Modal from '../Modal/Modal'
import IcoCheck from '../icons/IcoCheck'
import IcoCard from '../icons/IcoCard'
import { BtnGhost, BtnOrange, BtnPrimary } from '../ui/buttons'
import { Field, FieldError, FieldHint, Input, Label, Select } from '../ui/fields'
import { formatBRL, formatBRLParcela } from '../../data/pricing'
import { theme } from '../../styles'
import {
  Callout,
  SuccessBox,
  SuccessSeal,
  ErroMsg
} from './cancel-flow-styles'
import {
  Itemrow,
  ItemrowIcon,
  ItemrowBody,
  ItemrowTags,
  Tag
} from './payment-flow-styles'
import BrandMark from './BrandMark'
import InstagramCta from './InstagramCta'
import { PixWrap, PixQr, PixCodeRow, PixCode, PixCopyBtn } from './renew-flow-styles'

const PARCELAS = [
  { 'value': 1, 'label': '1x (à vista)' },
  { 'value': 6, 'label': '6x' },
  { 'value': 12, 'label': '12x' }
]

export default function ReactivateFlow( { idAssinatura, planoId, enderecoId, card, metodo, pixPendente, precoRenovacao, onReactivate, onClose } ) {
  const isCartao = metodo === 'card'
  const pixAproveitavel = isCartao ? null : pixPendente

  const [ cvv, setCvv ] = useState( '' )
  const [ parcelas, setParcelas ] = useState( 12 )
  const [ submitting, setSubmitting ] = useState( false )
  const [ erro, setErro ] = useState( false )
  const [ view, setView ] = useState( pixAproveitavel ? 'pix' : 'form' )
  const pixInicial = pixAproveitavel ? { 'codigo': pixAproveitavel.dados, 'url': pixAproveitavel.url } : null
  const [ pix, setPix ] = useState( pixInicial )
  const [ copiado, setCopiado ] = useState( false )

  const cvvInvalido = isCartao && cvv.length < 3
  const valorTotal = precoRenovacao ? precoRenovacao.totalAnual : null

  const montarBase = ( pagamento ) => ( {
    'assinatura_id': idAssinatura,
    'plano': { 'id': planoId },
    'entrega': { 'id': enderecoId },
    pagamento
  } )

  const confirmar = async() => {
    setErro( false )
    setSubmitting( true )
    try {
      if ( metodo === 'pix' ) {
        const data = await onReactivate( montarBase( { 'tipo': 'PIX' } ) )
        const dadosPix = data?.assinatura?.pagamento?.pix || {}
        setPix( { 'codigo': dadosPix.dados || '', 'url': dadosPix.url || '' } )
        setView( 'pix' )
      } else {
        await onReactivate( montarBase( {
          'tipo': 'CARTAO_CREDITO',
          parcelas,
          'cartao_credito': { 'id': card.id, cvv }
        } ) )
        setView( 'cardsuccess' )
      }
    } catch ( falha ) {
      setErro( true )
      setSubmitting( false )
    }
  }

  const avisarPagamentoPix = () => setView( 'pixavisado' )

  const copiarPix = () => {
    if ( pix && pix.codigo && navigator.clipboard ) {
      navigator.clipboard.writeText( pix.codigo )
      setCopiado( true )
      setTimeout( () => setCopiado( false ), 2500 )
    }
  }

  if ( view === 'cardsuccess' ) {
    return (
      <Modal
        footer={ (
          <BtnPrimary
            onClick={ onClose }
            type="button"
          >
            Fechar
          </BtnPrimary>
        ) }
        onClose={ onClose }
      >
        <SuccessBox>
          <SuccessSeal $orange>
            <IcoCheck
              height="22"
              style={{ 'color': '#fff' }}
              width="22"
            />
          </SuccessSeal>
          <h2 className="serif">Assinatura reativada</h2>
          <p>
            Sua assinatura do clube Bookster pelo Mundo foi reativada por mais um ano!
            Todas as informações referentes a sua nova jornada serão enviadas para o seu e-mail.
          </p>
          <p>Obrigado por seguir viagem com o clube!</p>
          <InstagramCta />
        </SuccessBox>
      </Modal>
    )
  }

  if ( view === 'pixavisado' ) {
    return (
      <Modal
        footer={ (
          <BtnPrimary
            onClick={ onClose }
            type="button"
          >
            Fechar
          </BtnPrimary>
        ) }
        onClose={ onClose }
      >
        <SuccessBox>
          <SuccessSeal $orange>
            <IcoCheck
              height="22"
              style={{ 'color': '#fff' }}
              width="22"
            />
          </SuccessSeal>
          <h2 className="serif">Recebemos seu aviso</h2>
          <p>
            Sua assinatura do clube Bookster pelo Mundo será reativada por mais um ano assim que
            o Pix cair! Todas as informações referentes a sua nova jornada serão enviadas para
            o seu e-mail.
          </p>
          <p>Obrigado por seguir viagem com o clube!</p>
          <InstagramCta />
        </SuccessBox>
      </Modal>
    )
  }

  if ( view === 'pix' ) {
    return (
      <Modal
        eyebrow="Reativar assinatura"
        footer={ (
          <BtnPrimary
            onClick={ avisarPagamentoPix }
            type="button"
          >
            Já paguei
          </BtnPrimary>
        ) }
        onClose={ avisarPagamentoPix }
        sub="Escaneie o QR Code ou use o Pix copia e cola. A reativação é confirmada assim que o pagamento cair."
        title="Pague com Pix para reativar"
      >
        <PixWrap>
          { pix && pix.url ? (
            <PixQr
              alt="QR Code do Pix"
              src={ pix.url }
            />
          ) : null }
          { pix && pix.codigo ? (
            <PixCodeRow>
              <PixCode>{ pix.codigo }</PixCode>
              <PixCopyBtn
                onClick={ copiarPix }
                type="button"
              >
                { copiado ? 'Copiado!' : 'Copiar' }
              </PixCopyBtn>
            </PixCodeRow>
          ) : null }
          <p>Também enviamos o Pix para o seu e-mail.</p>
        </PixWrap>
      </Modal>
    )
  }

  const cartaoIndisponivel = isCartao && !card
  const rotuloConfirmar = isCartao ? 'Confirmar reativação' : 'Gerar Pix'
  const footer = (
    <>
      <BtnGhost
        disabled={ submitting }
        onClick={ onClose }
        type="button"
      >Voltar
      </BtnGhost>
      <BtnOrange
        disabled={ submitting || cvvInvalido || cartaoIndisponivel }
        onClick={ confirmar }
        type="button"
      >
        { submitting ? 'Processando…' : rotuloConfirmar }
      </BtnOrange>
    </>
  )

  return (
    <Modal
      canClose={ !submitting }
      eyebrow="Assinatura"
      footer={ footer }
      onClose={ onClose }
      sub="Retome sua assinatura e volte a receber as próximas edições do clube."
      title="Reativar assinatura"
    >
      { cartaoIndisponivel ? (
        <Callout>
          <IcoCard
            height="20"
            style={{ 'color': theme.cores.statusStop, 'flex': 'none' }}
            width="20"
          />
          <span>
            Não encontramos um cartão cadastrado. Cadastre um cartão em
            <b> Formas de pagamento</b> antes de reativar.
          </span>
        </Callout>
      ) : null }

      { isCartao && card ? (
        <>
          <Itemrow
            $primary
            style={{ 'marginBottom': '18px' }}
          >
            <ItemrowIcon>
              <IcoCard
                height="20"
                width="20"
              />
            </ItemrowIcon>
            <ItemrowBody>
              <ItemrowTags>
                <BrandMark brand={ card.brand } />
                <Tag>principal</Tag>
              </ItemrowTags>
              <b>•••• { card.last4 }</b>
              { card.exp ? <span>Validade { card.exp }</span> : null }
            </ItemrowBody>
          </Itemrow>

          <Field>
            <Label>Parcelamento</Label>
            <Select
              disabled={ submitting }
              onChange={ ( event ) => setParcelas( Number( event.target.value ) ) }
              value={ parcelas }
            >
              { PARCELAS.map( ( item ) => (
                <option
                  key={ item.value }
                  value={ item.value }
                >
                  { item.label } — { formatBRLParcela( valorTotal, item.value ) }{ item.value === 1 ? '' : '/mês' }
                </option>
              ) ) }
            </Select>
          </Field>

          <Field>
            <Label>CVV do cartão</Label>
            <Input
              $err={ Boolean( erro && cvvInvalido ) }
              disabled={ submitting }
              inputMode="numeric"
              maxLength={ 4 }
              onChange={ ( event ) => setCvv( event.target.value.replace( /\D/gu, '' ).slice( 0, 4 ) ) }
              placeholder="000"
              style={{ 'maxWidth': '130px' }}
              value={ cvv }
            />
            { cvvInvalido && cvv.length > 0 ? (
              <FieldError>3 ou 4 dígitos</FieldError>
            ) : (
              <FieldHint>Confirme os 3 ou 4 dígitos do verso do cartão para autorizar a cobrança.</FieldHint>
            ) }
          </Field>

          { valorTotal ? (
            <Callout>
              <IcoCheck
                height="20"
                style={{ 'color': theme.cores.accent, 'flex': 'none' }}
                width="20"
              />
              <span>
                Total de <b>{ formatBRL( valorTotal ) }</b> em <b>{ parcelas }x de { formatBRLParcela( valorTotal, parcelas ) }</b>, cobrado no cartão principal.
              </span>
            </Callout>
          ) : null }
        </>
      ) : null }

      { !isCartao ? (
        <Callout>
          <IcoCheck
            height="20"
            style={{ 'color': theme.cores.accent, 'flex': 'none' }}
            width="20"
          />
          <span>Vamos gerar um Pix para você pagar agora e reativar na hora.</span>
        </Callout>
      ) : null }

      { erro ? (
        <ErroMsg>Não foi possível concluir a reativação agora. Confira os dados e tente novamente.</ErroMsg>
      ) : null }
    </Modal>
  )
}

ReactivateFlow.propTypes = {
  'idAssinatura': number.isRequired,
  'planoId': oneOfType( [ number, string ] ),
  'enderecoId': oneOfType( [ number, string ] ),
  'card': object,
  'metodo': string,
  'pixPendente': object,
  'precoRenovacao': shape( {
    'original': number,
    'promo': number,
    'totalAnual': number,
    'totalAnualCheio': number
  } ),
  'onReactivate': func.isRequired,
  'onClose': func.isRequired
}

ReactivateFlow.defaultProps = {
  'planoId': null,
  'enderecoId': null,
  'card': null,
  'metodo': 'card',
  'pixPendente': null,
  'precoRenovacao': null
}
